/**
 * The grown-up's view of Kid Money: every child at a glance, with the controls kids
 * don't get — move money between a kid's buckets, pay some out, and manage any kid's
 * goals. All of it is enforced again in the Worker; this is the convenient surface.
 */
import { useState } from 'react';
import { money } from '../../lib/api';
import type { KidGoal, MemberSummary } from '../../shared/types';
import GoalForm from './GoalForm';
import MoveForm from './MoveForm';
import PayoutForm from './PayoutForm';
import {
  Avatar,
  BUCKETS,
  BucketBar,
  Card,
  EmptyState,
  GoalBar,
  bucketMeta,
  btnPrimary,
  btnSecondary,
  cx,
  walletTotal,
  type KidSummary,
} from './shared';

type Dialog =
  | { kind: 'move'; kid: KidSummary }
  | { kind: 'payout'; kid: KidSummary }
  | { kind: 'goal-new'; member: MemberSummary }
  | { kind: 'goal-edit'; goal: KidGoal }
  | null;

export default function AdultView({
  summary,
  reload,
}: {
  summary: KidSummary[];
  reload: () => Promise<void>;
}) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  if (summary.length === 0) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState
          title="No kids set up yet"
          body="Add a child in Settings → Family, then their Spend / Save / Give buckets and goals show up here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Header />

      {summary.map((kid) => {
        const total = walletTotal(kid.wallet);
        return (
          <section key={kid.member.id} className="space-y-3">
            {/* Kid header + controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Avatar member={kid.member} />
                <div>
                  <h2 className="font-display text-lg font-semibold text-ink">{kid.member.name}</h2>
                  <p className="text-sm text-ink-soft">
                    {money(total)} total
                    {kid.wallet.pending > 0 ? ` · ${money(kid.wallet.pending)} pending` : ''}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDialog({ kind: 'move', kid })}
                  disabled={total <= 0}
                  className={btnSecondary}
                >
                  Move
                </button>
                <button
                  type="button"
                  onClick={() => setDialog({ kind: 'payout', kid })}
                  disabled={total <= 0}
                  className={btnSecondary}
                >
                  Pay out
                </button>
                <button
                  type="button"
                  onClick={() => setDialog({ kind: 'goal-new', member: kid.member })}
                  className={btnPrimary}
                >
                  + Goal
                </button>
              </div>
            </div>

            {/* Buckets */}
            <div className="grid gap-3 sm:grid-cols-3">
              {BUCKETS.map((b) => (
                <Card key={b.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
                      <span aria-hidden="true">{b.emoji}</span>
                      {b.label}
                    </span>
                    <span className={cx('font-display text-lg font-semibold', b.text)}>
                      {money(kid.wallet[b.key])}
                    </span>
                  </div>
                  <BucketBar meta={b} amount={kid.wallet[b.key]} total={total} />
                </Card>
              ))}
            </div>

            {/* Goals (compact, editable) */}
            {kid.goals.length > 0 ? (
              <ul className="grid gap-2 sm:grid-cols-2">
                {kid.goals.map((goal) => {
                  const meta = bucketMeta(goal.bucket);
                  const done = !!goal.achieved_at;
                  return (
                    <li key={goal.id}>
                      <Card className={cx('flex flex-col gap-2', done && 'border-sage')}>
                        <div className="flex items-center gap-2">
                          <span aria-hidden="true" className="text-xl">
                            {goal.emoji ?? '🎯'}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                            {goal.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => setDialog({ kind: 'goal-edit', goal })}
                            className="rounded-lg px-2 py-1 text-xs font-medium text-sage hover:bg-sage-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                          >
                            Edit
                          </button>
                        </div>
                        <GoalBar percent={goal.percent} accent={done ? 'var(--color-sage)' : meta.accent} />
                        <p className="text-xs text-ink-soft">
                          {done ? 'Reached 🎉 · ' : ''}
                          {money(goal.saved)} of {money(goal.target)} · {meta.label}
                        </p>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-ink-faint">No goals yet.</p>
            )}
          </section>
        );
      })}

      {dialog?.kind === 'move' ? (
        <MoveForm member={dialog.kid.member} wallet={dialog.kid.wallet} onClose={close} onSaved={reload} />
      ) : null}
      {dialog?.kind === 'payout' ? (
        <PayoutForm member={dialog.kid.member} wallet={dialog.kid.wallet} onClose={close} onSaved={reload} />
      ) : null}
      {dialog?.kind === 'goal-new' ? (
        <GoalForm memberId={dialog.member.id} onClose={close} onSaved={reload} />
      ) : null}
      {dialog?.kind === 'goal-edit' ? (
        <GoalForm goal={dialog.goal} memberId={dialog.goal.member_id} onClose={close} onSaved={reload} />
      ) : null}
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Kid Money</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Spend, Save and Give buckets, and each kid’s savings goals.
      </p>
    </div>
  );
}
